// Small non-blocking notifications ("toasts") that replace alert() pop-ups,
// which freeze the screen. A module-level store, so any hook or component can
// call toast.error(...) without a provider; <Toaster /> shows them.

const DURATION = { info: 4000, success: 3000, error: 7000 };
const MAX_VISIBLE = 4;

let toasts = [];
let nextId = 1;
const listeners = new Set();
const timers = new Map();

const emit = () => listeners.forEach(fn => fn(toasts));

export const getToasts = () => toasts;

export function subscribeToasts(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function dismissToast(id) {
  clearTimeout(timers.get(id));
  timers.delete(id);
  if (!toasts.some(t => t.id === id)) return;
  toasts = toasts.filter(t => t.id !== id);
  emit();
}

export function clearToasts() {
  timers.forEach(clearTimeout);
  timers.clear();
  toasts = [];
  emit();
}

/** Shows a message; the same message already on screen is not stacked again. */
export function showToast(message, type = 'info') {
  const text = String(message);
  const existing = toasts.find(t => t.message === text && t.type === type);
  if (existing) {
    clearTimeout(timers.get(existing.id));
    timers.set(existing.id, setTimeout(() => dismissToast(existing.id), DURATION[type]));
    return existing.id;
  }
  const id = nextId++;
  toasts = [...toasts, { id, message: text, type }].slice(-MAX_VISIBLE);
  timers.set(id, setTimeout(() => dismissToast(id), DURATION[type] || DURATION.info));
  emit();
  return id;
}

export const toast = {
  info: (message) => showToast(message, 'info'),
  success: (message) => showToast(message, 'success'),
  error: (message) => showToast(message, 'error'),
};
